import React from 'react';
import ReactDOM from 'react-dom';
import cs from 'classnames';
import {
  useFloating,
  offset as offsetMw,
  flip as flipMw,
  shift as shiftMw,
  arrow as arrowMw,
  autoPlacement as autoPlacementMw,
  Placement,
} from '@floating-ui/react';

const DEFAULT_NAME = 'k-ui-popover';

export type PlacementType =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'bottom'
  | 'right'
  | 'left'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end';

export interface PopoverProps {
  /**
   * 悬浮卡片内容
   */
  content: React.ReactNode;
  /**
   * 手动控制卡片显隐
   */
  visible: boolean;
  /**
   * 点击卡片外回调
   */
  onClickAway?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * 悬浮卡片是否需要蒙层
   */
  needMask?: boolean;
  /**
   * 卡片弹出的位置
   */
  placement?: PlacementType;
  /**
   * 卡片弹出位置的备选
   */
  fallbackPlacements?: PlacementType[];
  /**
   * 自定义卡片与参考元素边距
   */
  offset?: [number, number];
  /**
   * 自定义class
   */
  className?: string;
  /**
   * 叠加顺序
   */
  zIndex?: number;
  children?: React.ReactNode;
  /**
   * 外部变量传入的referenceElement
   */
  refEle?: HTMLElement | null;
  arrow?: {
    element?: HTMLElement | string;
    padding?: Padding;
  };
}

type Padding =
  | number
  | {
      top?: number;
      left?: number;
      right?: number;
      bottom?: number;
    };

interface PopoverRef {
  update: () => void;
}

export const Popover = React.forwardRef<PopoverRef, PopoverProps>(
  (props, ref) => {
    const {
      content,
      visible,
      needMask = true,
      placement = 'right-start',
      fallbackPlacements,
      offset = [0, 0],
      className = '',
      zIndex = 1000,
      onClickAway,
      arrow,
    } = props;

    const [referenceElement, setReferenceElement] =
      React.useState<HTMLElement | null>(null);

    // 解析 auto / auto-start / auto-end
    const isAuto = placement.startsWith('auto');
    const autoAlign: 'start' | 'end' | undefined =
      placement === 'auto-start'
        ? 'start'
        : placement === 'auto-end'
          ? 'end'
          : undefined;

    // 解析 offset: [skidding, distance] -> { crossAxis, mainAxis }
    const [skidding, distance] = offset;

    // 解析 arrow.element 支持 HTMLElement 或选择器字符串
    const [arrowEl, setArrowEl] = React.useState<HTMLElement | null>(null);
    React.useEffect(() => {
      if (!arrow?.element) {
        setArrowEl(null);
        return;
      }
      if (typeof arrow.element !== 'string') {
        setArrowEl(arrow.element ?? null);
        return;
      }
      const el = document.querySelector(arrow.element);
      setArrowEl((el as HTMLElement) ?? null);
    }, [arrow?.element]);

    const middleware = React.useMemo(() => {
      const mws: any[] = [];

      // offset: mainAxis 为距离，crossAxis 为侧向偏移
      mws.push(
        offsetMw({
          mainAxis: distance,
          crossAxis: skidding,
        }),
      );

      if (isAuto) {
        mws.push(
          autoPlacementMw({
            alignment: autoAlign,
            // 若提供了 fallbackPlacements，自动放置会参考这些方向
            // floating-ui 的 autoPlacement 会自动选择最佳位置，这里不显式传入候选方向
          }),
        );
      } else {
        mws.push(
          flipMw({
            fallbackPlacements: fallbackPlacements as Placement[] | undefined,
          }),
        );
      }

      mws.push(
        shiftMw({
          padding: 10,
          crossAxis: true,
        }),
      );

      if (arrow && arrowEl) {
        mws.push(
          arrowMw({
            element: arrowEl,
            padding: arrow.padding as any,
          }),
        );
      }
      return mws;
    }, [
      distance,
      skidding,
      isAuto,
      autoAlign,
      fallbackPlacements,
      arrow,
      arrowEl,
    ]);

    const { x, y, strategy, refs, update } = useFloating({
      placement: (isAuto ? 'bottom' : (placement as Placement)) as Placement,
      middleware,
    });

    // 支持外部 refEle
    React.useEffect(() => {
      if (props.refEle) {
        refs.setReference(props.refEle);
      }
    }, [props.refEle, refs]);

    React.useImperativeHandle(ref, () => ({
      update: () => {
        if (update) update();
      },
    }));

    const containerStyle: any = needMask
      ? {
          position: 'absolute',
          top: '0px',
          left: '0px',
          right: '0px',
          bottom: '0px',
          width: '100%',
          WebkitAppRegion: 'no-drag',
          zIndex,
        }
      : undefined;

    const floatingStyle: React.CSSProperties = {
      position: strategy,
      top: y ?? 0,
      left: x ?? 0,
      zIndex,
    };

    const [shouldRender, setShouldRender] = React.useState(false);
    React.useEffect(() => {
      if (visible) {
        // 延迟一帧渲染，确保位置计算完成
        requestAnimationFrame(() => {
          setShouldRender(true);
        });
      } else {
        setShouldRender(false);
      }
    }, [visible]);

    return (
      <>
        {props.children ? (
          <div
            ref={(node) => {
              setReferenceElement(node);
              refs.setReference(node);
            }}
            tabIndex={0}
            aria-label="popover-reference"
            role="button"
          >
            {props.children}
          </div>
        ) : null}
        {shouldRender
          ? ReactDOM.createPortal(
              <div
                className={cs(`${DEFAULT_NAME}`, className)}
                style={containerStyle}
                onClick={onClickAway}
              >
                <div
                  ref={refs.setFloating}
                  style={floatingStyle}
                  {...(isAuto ? {} : {})}
                  tabIndex={0}
                  aria-label="popover-content"
                  role="dialog"
                >
                  {content}
                </div>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  },
);
